import React, { forwardRef, ReactNode, useContext, useEffect, useImperativeHandle, useState } from 'react';
import './DemographicsFilter.scss';
import {
    DefaultConfigWizardContext,
    DefaultConfigWizardContextValue,
} from '../../DefaultsConfigWizard/DefaultsConfigWizardContext';
import { DemographicsRunConfigOptions } from '@/types/RunConfigOptions';
import { useUser } from '@/contexts/UserContext';
import { FilterGroup } from '@/components/ConfigurationEditors/FilterGroup';
import classnames from 'classnames';
import { DemographicDimensionSelect } from '@/components/DemographicDimensionSelect';
import { DefaultConfigLabels } from '@/pages/Onboarding/OnboardingSteps';

export type DemographicsFiltersRef = {
    value?: DefaultConfigWizardContextValue['value'];
};

export type DemographicsFiltersProps = {
    name?: string;
    onChange?: (e: ByzzerChangeEvent<DefaultConfigWizardContextValue['value']>) => void;
    value?: DefaultConfigWizardContextValue['value'];
    summary?: ReactNode;
    labelsAndTips?: DefaultConfigLabels;
} & Partial<Omit<DemographicsRunConfigOptions, 'type' | 'title'>> &
    Partial<Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>>;

export const DemographicsFilter = forwardRef<DemographicsFiltersRef, DemographicsFiltersProps>(
    ({ className, name, value, onChange, summary, includeDemographicDimensions = true, maxDemographicDimensions, sku, ...props }, ref) => {
        const baseClassName = 'byz-demographics-filters';

        const { value: contextValue, onChange: onContextChange } = useContext(DefaultConfigWizardContext);

        const [internalValue, setInternalValue] = useState<DefaultConfigWizardContextValue['value']>({});

        const { defaultRunConfig } = useUser();

        function handleChange(e: ByzzerChangeEvent<unknown>) {
            onContextChange?.(e.name as keyof typeof defaultRunConfig, e.value, e.data);
        }

        useImperativeHandle(ref, () => ({
            get value() {
                return internalValue;
            },
        }));

        useEffect(() => {
            setInternalValue(contextValue ?? {});
        }, [contextValue]);

        useEffect(() => {
            if (value) {
                setInternalValue(value);
            }
        }, [value]);

        return (
            <FilterGroup className={classnames(baseClassName, className)}>
                {summary && <div className={`${baseClassName}__summary`}>{summary}</div>}

                <DemographicDimensionSelect
                    onlyRenderIf={includeDemographicDimensions}
                    name={'demographicDimensions'}
                    value={internalValue.demographicDimensions}
                    onChange={handleChange}
                    placeholder={'Select Demographics'}
                    maxSelections={maxDemographicDimensions}
                    sku={sku}
                />
            </FilterGroup>
        );
    }
);

export default DemographicsFilter;
DemographicsFilter.displayName = 'DemographicsFilter';
